import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Group,
  Stack,
  Text,
  Title,
  Paper,
  Flex,
  Select,
  MultiSelect,
  Loader,
  Grid
} from '@mantine/core';
import { IconPlus, IconFileImport, IconUsersGroup } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { masterQuestionService } from 'services/lia/masterQuestionService';

/* eslint-disable */
interface QuestionOptionModalProps {
  opened: boolean;
  onClose: () => void;
  onSelectCreateNew: (
    moduleId: string,
    respondentTypes: string[],
    externalTypeIds?: string[]
  ) => void;
  onSelectFromGroup: (
    moduleId: string,
    respondentTypes: string[],
    externalTypeIds?: string[]
  ) => void;
  onSelectImport: (moduleId: string, respondentTypes: string[], externalTypeIds?: string[]) => void;
  selectedModules: string[];
  modules: any[];
}
/* eslint-enable */

const QuestionOptionModal: React.FC<QuestionOptionModalProps> = ({
  opened,
  onClose,
  onSelectCreateNew,
  onSelectFromGroup,
  onSelectImport,
  selectedModules,
  modules
}) => {
  const [selectedModule, setSelectedModule] = useState<string>('');
  const [selectedRespondentTypes, setSelectedRespondentTypes] = useState<string[]>([]);
  const [selectedExternalTypes, setSelectedExternalTypes] = useState<string[]>([]);

  // Filter modules to only show selected ones
  const availableModulesForQuestions = modules.filter((m) => selectedModules.includes(m.moduleId));

  // Check if External is selected
  const isExternalSelected = selectedRespondentTypes.includes('External');

  // Fetch external question types when External is selected
  const { data: externalTypesData, isLoading: isLoadingExternalTypes } = useQuery({
    queryKey: ['externalQuestionTypes'],
    queryFn: () => masterQuestionService.getExternalQuestionTypeList(),
    enabled: isExternalSelected && opened,
    staleTime: 5 * 60 * 1000 // Cache for 5 minutes
  });

  // Format external types options for dropdown
  const externalTypeOptions =
    externalTypesData?.data?.map((type: any) => ({
      value: type.msExternalQuestionTypeID,
      label: type.questionExternalType
    })) || [];

  // Reset selections when modal is closed
  useEffect(() => {
    if (!opened) {
      setSelectedModule('');
      setSelectedRespondentTypes([]);
      setSelectedExternalTypes([]);
    }
  }, [opened]);

  const respondentTypes = [
    { value: 'Self', label: 'Self Assessment' },
    { value: 'Peer', label: 'Peer Assessment' },
    { value: 'Supervisor', label: 'Supervisor Assessment' },
    { value: 'Subordinate', label: 'Subordinate Assessment' },
    { value: 'External', label: 'External Assessment' }
  ];

  const handleCreateNew = () => {
    if (!selectedModule || selectedRespondentTypes.length === 0) {
      return;
    }

    // If External is selected but no external types are selected, show error
    if (isExternalSelected && selectedExternalTypes.length === 0) {
      alert('Please select at least one external type');
      return;
    }

    onSelectCreateNew(selectedModule, selectedRespondentTypes, selectedExternalTypes);
  };

  const handleSelectFromGroup = () => {
    if (!selectedModule || selectedRespondentTypes.length === 0) {
      return;
    }

    // If External is selected but no external types are selected, show error
    if (isExternalSelected && selectedExternalTypes.length === 0) {
      alert('Please select at least one external type');
      return;
    }

    onSelectFromGroup(selectedModule, selectedRespondentTypes, selectedExternalTypes);
  };

  const handleImport = () => {
    if (!selectedModule || selectedRespondentTypes.length === 0) {
      return;
    }

    // If External is selected but no external types are selected, show error
    if (isExternalSelected && selectedExternalTypes.length === 0) {
      alert('Please select at least one external type');
      return;
    }

    onSelectImport(selectedModule, selectedRespondentTypes, selectedExternalTypes);
  };

  // Selection is complete if module and respondent types are selected,
  // and if External is selected, external types must also be selected
  const isSelectionComplete =
    selectedModule &&
    selectedRespondentTypes.length > 0 &&
    (!isExternalSelected || (isExternalSelected && selectedExternalTypes.length > 0));

  return (
    <Modal opened={opened} onClose={onClose} withCloseButton={false} centered size='xl'>
      <Stack gap='sm' p={20}>
        <Title order={3}>Add Question</Title>
        <Text c='dimmed' size='sm' mt={-5}>
          Please select module and respondent type for your question
        </Text>

        <Select
          label='Select Module'
          placeholder='Choose a module'
          data={availableModulesForQuestions.map((m) => ({
            value: m.moduleId,
            label: m.moduleName
          }))}
          value={selectedModule}
          onChange={(value) => setSelectedModule(value || '')}
          required
        />

        <MultiSelect
          label='Question is for'
          placeholder='Select respondent types'
          data={respondentTypes}
          value={selectedRespondentTypes}
          onChange={(values) => setSelectedRespondentTypes(values)}
          required
        />

        {isExternalSelected && (
          <MultiSelect
            label='External Assessment Types'
            placeholder={
              isLoadingExternalTypes ? 'Loading external types...' : 'Select external types'
            }
            data={externalTypeOptions}
            value={selectedExternalTypes}
            onChange={setSelectedExternalTypes}
            disabled={isLoadingExternalTypes}
            rightSection={isLoadingExternalTypes ? <Loader size='xs' /> : null}
            required
          />
        )}

        <Grid grow mt='lg'>
          {/* Create New Option */}
          <Grid.Col span={4}>
            <Paper
              shadow='sm'
              p='md'
              withBorder
              radius='md'
              onClick={handleCreateNew}
              style={{
                cursor: isSelectionComplete ? 'pointer' : 'not-allowed',
                opacity: isSelectionComplete ? 1 : 0.6,
                transition: 'all 0.2s ease',
                height: '120px',
                '&:hover': {
                  transform: isSelectionComplete ? 'translateY(-2px)' : 'none',
                  boxShadow: isSelectionComplete ? '0 4px 8px rgba(0,0,0,0.1)' : 'none'
                }
              }}
            >
              <Flex direction='column' align='center' justify='center' h='100%' gap='sm'>
                <IconPlus size={32} color={isSelectionComplete ? '#7B2183' : '#868e96'} />
                <Text size='md' fw={600} ta='center'>
                  Create New Question
                </Text>
                <Text size='xs' c='dimmed' ta='center'>
                  Start from scratch
                </Text>
              </Flex>
            </Paper>
          </Grid.Col>

          {/* Select from Group Option */}
          <Grid.Col span={4}>
            <Paper
              shadow='sm'
              p='md'
              withBorder
              radius='md'
              onClick={handleSelectFromGroup}
              style={{
                cursor: isSelectionComplete ? 'pointer' : 'not-allowed',
                opacity: isSelectionComplete ? 1 : 0.6,
                transition: 'all 0.2s ease',
                height: '120px',
                '&:hover': {
                  transform: isSelectionComplete ? 'translateY(-2px)' : 'none',
                  boxShadow: isSelectionComplete ? '0 4px 8px rgba(0,0,0,0.1)' : 'none'
                }
              }}
            >
              <Flex direction='column' align='center' justify='center' h='100%' gap='sm'>
                <IconUsersGroup size={32} color={isSelectionComplete ? '#7B2183' : '#868e96'} />
                <Text size='md' fw={600} ta='center'>
                  Choose From Group
                </Text>
                <Text size='xs' c='dimmed' ta='center'>
                  Select from question bank
                </Text>
              </Flex>
            </Paper>
          </Grid.Col>

          {/* Import from File Option */}
          <Grid.Col span={4}>
            <Paper
              shadow='sm'
              p='md'
              withBorder
              radius='md'
              onClick={handleImport}
              style={{
                cursor: isSelectionComplete ? 'pointer' : 'not-allowed',
                opacity: isSelectionComplete ? 1 : 0.6,
                transition: 'all 0.2s ease',
                height: '120px',
                '&:hover': {
                  transform: isSelectionComplete ? 'translateY(-2px)' : 'none',
                  boxShadow: isSelectionComplete ? '0 4px 8px rgba(0,0,0,0.1)' : 'none'
                }
              }}
            >
              <Flex direction='column' align='center' justify='center' h='100%' gap='sm'>
                <IconFileImport size={32} color={isSelectionComplete ? '#7B2183' : '#868e96'} />
                <Text size='md' fw={600} ta='center'>
                  Import from Excel
                </Text>
                <Text size='xs' c='dimmed' ta='center'>
                  Upload questions file
                </Text>
              </Flex>
            </Paper>
          </Grid.Col>
        </Grid>

        <Group justify='flex-end' mt='md'>
          <Button variant='outline' color='#7B2183' onClick={onClose}>
            Cancel
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default QuestionOptionModal;
